










































































































import {
  computed, defineComponent, reactive, ref,
} from '@vue/composition-api';
import { useFormat } from '@ideavivat/vivat-2';

import { getPurchaseOrder } from '@/api';
import { PurchaseOrder } from '@/types/purchase-order';
import useAddress from '@/use/useAddress';
import usePurchaseOrder from '@/use/usePurchaseOrder';
import itemTableOption from '@/table/PurchaseOrder/purchase-order-modal-items';
import miscTableOption from '@/table/PurchaseOrder/purchase-order-modal-misc';
import useAuth from '@/use/useAuth';
import useDownloadReport from '@/use/useDownloadReport';

import Modal from '@/components/PurchaseOrder/Modal.vue';

const NOW = useFormat.date().computerShortDate();
const defaultPurchaseOrder = {
  id: -1,
  bomId: -1,
  code: '',
  type: 'MATERIALS',
  status: 'PENDING',
  createdAt: NOW,
  updatedAt: NOW,
  deletedAt: NOW,
  remarks: '',
  userId: -1,
  user: { id: -1 },
  items: [],
  miscItems: [],
  invoice: '',
  approved: false,
  manager_approved: false,
  chief_manager_approved: false,
  vat: 0,
  vat_type: 'NONE',
  total_price: 0,
  quotation_no: '',
  discount: 0,
  supplier: undefined,
  jobNo: '',
  jobName: '',
  poCreator: '',
};

const CommonModalWithPurchaseRequestInfo = defineComponent({
  components: {
    Modal,
  },
  setup(props, { emit, root }) {
    const { statusClassMap, statusTranslate } = usePurchaseOrder();
    const { getReport } = useDownloadReport();
    const forceRender = ref(true);

    const { hasPermission: checkPermissionHandler } = useAuth();
    function hasPermission(name: string) {
      return checkPermissionHandler(root.$store.getters.me, name);
    }
    const modal = ref(null);
    const purchaseOrder: PurchaseOrder = reactive({ ...defaultPurchaseOrder });

    async function prepare(id: number) {
      const result = (await getPurchaseOrder(id)).data;
      forceRender.value = false;
      purchaseOrder.id = result.id;
      purchaseOrder.code = result.code;
      purchaseOrder.type = result.type;
      purchaseOrder.status = result.status;
      purchaseOrder.remarks = result.remarks;
      // purchaseOrder.targetDate = result.target_date;
      purchaseOrder.createdAt = result.created_at;
      purchaseOrder.updatedAt = result.updated_at;
      purchaseOrder.deletedAt = result.deleted_at;
      purchaseOrder.userId = result.user_id;
      purchaseOrder.user = result.user;
      purchaseOrder.items = result?.items || [];
      if (result.command_quantity > 0) {
        (purchaseOrder.items || []).splice(0, 0, {
          bom_item: { job_no: '', production_type: 'FAB', drawing_no: result.command },
          price_per_unit: result.command_unit_price,
          purchase_unit: result.command_unit,
          amount: result.command_quantity,
          total_price: result.command_unit_price * result.command_quantity,
        } as any);
      }
      purchaseOrder.miscItems = result.misc_items || [];
      purchaseOrder.invoice = result.invoice;
      purchaseOrder.supplier = {
        ...result.supplier,
        address: result.supplier?.address || null,
      };
      purchaseOrder.jobNo = result.job_no;
      purchaseOrder.jobName = result.job_name;
      purchaseOrder.poCreator = result.creator?.name || '';
      purchaseOrder.approved = result.approved;
      purchaseOrder.manager_approved = result.manager_approved;
      purchaseOrder.chief_manager_approved = result.chief_manager_approved;
      purchaseOrder.vat = result.vat;
      purchaseOrder.vat_type = result.vat_type;
      purchaseOrder.total_price = result.total_price;
      purchaseOrder.quotation_no = result.quotation_no;
      purchaseOrder.discount = result.discount;
      forceRender.value = true;
    }

    async function open(id: number) {
      await prepare(id);
      (modal.value as any).open();
    }
    const computedSupplierAddress = computed(() => useAddress().getAddressString(purchaseOrder.supplier));

    function close() {
      purchaseOrder.id = defaultPurchaseOrder.id;
      purchaseOrder.code = defaultPurchaseOrder.code;
      purchaseOrder.type = defaultPurchaseOrder.type;
      purchaseOrder.status = defaultPurchaseOrder.status;
      purchaseOrder.createdAt = defaultPurchaseOrder.createdAt;
      purchaseOrder.updatedAt = defaultPurchaseOrder.updatedAt;
      purchaseOrder.deletedAt = defaultPurchaseOrder.deletedAt;
      purchaseOrder.userId = defaultPurchaseOrder.userId;
      purchaseOrder.user = defaultPurchaseOrder.user;
      purchaseOrder.items = defaultPurchaseOrder.items;
      purchaseOrder.miscItems = defaultPurchaseOrder.miscItems;
      purchaseOrder.approved = defaultPurchaseOrder.approved;
      purchaseOrder.manager_approved = defaultPurchaseOrder.manager_approved;
      purchaseOrder.chief_manager_approved = defaultPurchaseOrder.chief_manager_approved;
      purchaseOrder.vat = defaultPurchaseOrder.vat;
      purchaseOrder.total_price = defaultPurchaseOrder.total_price;
      purchaseOrder.vat_type = defaultPurchaseOrder.vat_type;
      purchaseOrder.supplier = null as any;
      purchaseOrder.jobName = defaultPurchaseOrder.jobName;
      purchaseOrder.jobNo = defaultPurchaseOrder.jobNo;
      purchaseOrder.poCreator = defaultPurchaseOrder.poCreator;

      (modal.value as any).close();
    }

    const computedTableInformation = computed(() => {
      if (purchaseOrder.type === 'MATERIALS') return { data: purchaseOrder.items, option: itemTableOption };
      return { data: purchaseOrder.miscItems, option: miscTableOption };
    });

    // use this to manually paginate pr item;
    const page = ref(1);
    const PERPAGE = 200;
    const purchaseOrderItemPaginator = computed(() => ({
      perPage: PERPAGE,
      total: computedTableInformation.value.data?.length,
      from: (page.value - 1) * PERPAGE,
      to: (page.value * PERPAGE),
      currentPage: 1,
      lastPage: computedTableInformation.value.data?.length === 0 ? 1 : Math.ceil((computedTableInformation.value.data as any).length / PERPAGE),
      totalPages: computedTableInformation.value.data?.length === 0 ? 1 : Math.ceil((computedTableInformation.value.data as any).length / PERPAGE),
    }));
    const displayData = computed(() => {
      if ((computedTableInformation.value.data as any).length < purchaseOrderItemPaginator.value.to) return (computedTableInformation.value.data as any).slice(purchaseOrderItemPaginator.value.from, (computedTableInformation.value.data as any).length);
      return (computedTableInformation.value.data as any).slice(purchaseOrderItemPaginator.value.from, purchaseOrderItemPaginator.value.to);
    });

    const computedPurchaseOrderPrice = computed(() => {
      const totalPrice = purchaseOrder.total_price || 0;
      const totalVat = purchaseOrder.vat && purchaseOrder.vat > 0 ? (totalPrice * 7) / 100 : 0;

      return { totalPrice, totalVat };
    });

    async function approvePurchaseOrderAction() {
      emit('pendingApproval', purchaseOrder);
    }
    const computedApprovalRenderCondition = computed(() => {
      if (!purchaseOrder.approved) {
        return {
          approve: purchaseOrder.approved,
          level: 'อนุมัติสั่งซื้อ',
          canApprove: hasPermission('purchase_orders.approve'),
        };
      }
      if (!purchaseOrder.manager_approved) {
        return {
          approve: purchaseOrder.manager_approved,
          level: 'ผู้จัดการอนุมัติสั่งซื้อ',
          canApprove: hasPermission('purchase_orders.managerApprove'),
        };
      }
      if (!purchaseOrder.chief_manager_approved) {
        return {
          approve: purchaseOrder.chief_manager_approved,
          level: 'ผู้บริหารอนุมัติสั่งซื้อ',
          canApprove: hasPermission('purchase_orders.chiefManagerApprove'),
        };
      }
      return {
        approve: false,
        level: '',
        canApprove: false,
      };
    });

    return {
      forceRender,
      hasPermission,
      getReport,
      open,
      close,
      modal,
      statusClassMap,
      statusTranslate,

      purchaseOrder,
      computedTableInformation,
      page,
      PERPAGE,
      pagination: purchaseOrderItemPaginator,
      displayData,
      computedSupplierAddress,

      approvePurchaseOrderAction,
      computedApprovalRenderCondition,
      computedPurchaseOrderPrice,
    };
  },
});

export default CommonModalWithPurchaseRequestInfo;
